import { type ReactNode, useEffect, useState } from "react";

import { Auth } from "aws-amplify";
import moment from "moment";

import { Text } from "@alpacahq/ui/old";

import { useGetTradingAccount } from "~/v2/api/hooks";
import { Card } from "~/v2/components";
import { FullScreenSpinner } from "~/v2/components/common";
import { MFASettings } from "~/v2/components/MFASettings/MFASettings";
import { useMFA } from "~/v2/hooks/useMFA";

const MFA_ENFORCED_DATE = "2024-06-13";

const shouldEnforceMFA = (
  account: { created_at?: string } | undefined,
  cognitoUser: { preferredMFA?: string } | null,
) => {
  if (!account?.created_at || !cognitoUser) {
    return false;
  }
  return (
    cognitoUser.preferredMFA === "NOMFA" &&
    moment(account.created_at).isSameOrAfter(MFA_ENFORCED_DATE, "day")
  );
};

interface MFAGuardProps {
  children: ReactNode;
  requireMFAForAllAccounts?: boolean;
}

export const MFAGuard = ({
  children,
  requireMFAForAllAccounts = false,
}: MFAGuardProps) => {
  const { account } = useGetTradingAccount();
  const mfa = useMFA();
  const [cognitoUser, setCognitoUser] = useState<{
    preferredMFA?: string;
  } | null>(null);

  useEffect(() => {
    if (requireMFAForAllAccounts) {
      return;
    }

    Auth.currentAuthenticatedUser()
      .then((user) => setCognitoUser(user))
      .catch(() => {});
  }, [requireMFAForAllAccounts]);

  if (
    (requireMFAForAllAccounts && mfa.isMFALoading) ||
    (!requireMFAForAllAccounts && !account?.created_at)
  ) {
    return <FullScreenSpinner />;
  }

  if (
    (requireMFAForAllAccounts && !mfa.isMFAEnabled) ||
    (!requireMFAForAllAccounts && shouldEnforceMFA(account, cognitoUser))
  ) {
    return (
      <Card title="Secure Your Account" className="mr-8">
        <Text className="text-gray-500 text-md">
          Before you get started, please enable multi-factor authentication.
        </Text>
        <MFASettings
          mfaMethod={mfa.method}
          TOTPQRCode={mfa.TOTPQRCode}
          TOTPSetupVerificationError={mfa.TOTPSetupVerificationError}
          onRefreshMFA={mfa.refresh}
          onDisableMFA={mfa.disable}
          onSetupTOTP={mfa.setupTOTP}
          onVerifyTOTPToken={mfa.verifyTOTPToken}
        />
      </Card>
    );
  }

  return <>{children}</>;
};
